// frontend/src/pages/Home.js
import React, { useEffect, useState } from "react";
import "../styles/Home.css";
import { Link } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { formatRaceDate } from "../utils/dateUtils";
import sponsorsData from "../data/SponsorsData";
import galleryPhotos from "../data/GalleryData";
import { buildStandingsFromFlatResults, DIVISIONS} from "../utils/standingUtils";

const Home = () => {

    // Featured Sponsors
    const FEATURED_TIERS = [
        "Victory Lane Partner",
        "Pole Position Partner",
        "Green Flag Partner",
        "Pre-Race Tech Partner",
    ];

    const featuredSponsors = (sponsorsData || []).filter((s) =>
        FEATURED_TIERS.includes(s.tier)
    );

    // 🏁 Upcoming races
    const [upcomingRaces, setUpcomingRaces] = useState([]);
    const [loadingRaces, setLoadingRaces] = useState(true);
    const [raceError, setRaceError] = useState("");

    // 🏆 Championship standings
    const [standings, setStandings] = useState([]);
    const [loadingStandings, setLoadingStandings] = useState(true);
    const [standingsError, setStandingsError] = useState("");

    const getMedal = (place) => {
        if (place === 1) return "🥇";
        if (place === 2) return "🥈";
        if (place === 3) return "🥉";
        return "";
    };

    // ✅ Static home photo preview: ONLY 5 photos
    const homePhotos = (galleryPhotos || []).slice(0, 5);

    // 🔄 Load upcoming races
    useEffect(() => {
        const loadRaces = async () => {
            try {
                setLoadingRaces(true);
                setRaceError("");

                const res = await apiClient.get("/races");
                const mapped = (res.data || []).map((race) => ({
                    id: race.id,
                    name: race.raceName,
                    date: race.raceDate,
                    location: race.location,
                    description: race.description,
                }));

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const sorted = mapped
                    .filter((r) => !!r.date)
                    .filter((r) => {
                        const d = new Date(r.date);
                        if (Number.isNaN(d.getTime())) return false; // ignore bad dates
                        d.setHours(0, 0, 0, 0);
                        return d >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 3);

                setUpcomingRaces(sorted);
            } catch (err) {
                console.error("Error loading races:", err);
                setRaceError("Could not load upcoming races. Please try again later.");
            } finally {
                setLoadingRaces(false);
            }
        };

        void loadRaces();
    }, []);

    // 🔄 Load standings
    useEffect(() => {
        const loadStandings = async () => {
            try {
                setLoadingStandings(true);
                setStandingsError("");

                const res = await apiClient.get("/results");
                const flat = res.data || [];

                const standingsByDivision = buildStandingsFromFlatResults(flat);

                // Turn into the same structure your Home UI expects:
                // [{ division, leaders: [{ position, name, points }] }]
                const standingsArr = DIVISIONS.map((division) => {
                    const leaders = (standingsByDivision[division] || [])
                        .slice(0, 3)
                        .map((r, idx) => ({
                            position: idx + 1,
                            name: r.name,
                            points: r.points,
                        }));

                    return { division, leaders };
                });

                setStandings(standingsArr);
            } catch (err) {
                console.error("Error loading standings:", err);
                setStandingsError("Could not load championship leaders right now.");
            } finally {
                setLoadingStandings(false);
            }
        };

        void loadStandings();
    }, []);

    return (
        <div className="home-container">
            <section className="home-intro">
                <h1>Welcome to Lil Rockstars Racing</h1>
            </section>

            {/* === UPCOMING RACES === */}
            <section className="home-section">
                <h2>🏁 Upcoming Races 🏁</h2>

                {loadingRaces && <p>Loading...</p>}
                {raceError && <p>{raceError}</p>}
                {!loadingRaces && !raceError && upcomingRaces.length === 0 && <p>No races found.</p>}

                <div className="race-preview-grid">
                    {upcomingRaces.map((race) => (
                        <div key={race.id} className="race-card">
                            <h3>{race.name}</h3>
                            <p>📅 {formatRaceDate(race.date)}</p>
                            <p>📍 {race.location}</p>
                            <p className="desc">{race.description}</p>
                        </div>
                    ))}
                </div>

                <Link to="/races" className="view-all-link">
                    View All Races →
                </Link>
            </section>

            {/* === STANDINGS === */}
            <section className="home-section standings-preview">
                <h2>🏆 Championship Leaders 🏆</h2>

                {loadingStandings && <p>Loading...</p>}
                {standingsError && <p>{standingsError}</p>}
                {!loadingStandings && !standingsError && standings.length === 0 && <p>No standings yet.</p>}

                <div className="standings-grid">
                    {standings.map((s, idx) => (
                        <div key={idx} className="standing-card">
                            <h4 className="division-title">{s.division}</h4>
                            <ol className="leader-list">
                                {s.leaders.map((leader) => (
                                    <li key={leader.position}>
                                        <span style={{ marginRight: "6px" }}>{getMedal(leader.position)}</span>
                                        #{leader.position} {leader.name} — {leader.points} pts
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>

                <Link to="/results" className="view-all-link">
                    View Full Standings →
                </Link>
            </section>

            {/* === PHOTO PREVIEW (STATIC - 5 ONLY) === */}
            <section className="home-section gallery-preview">
                <h2>📸 LRR Highlights 📸</h2>

                <div className="photo-carousel">
                    {homePhotos.map((photo) => (
                        <img
                            key={photo.id}
                            src={photo.imageUrl}
                            alt={photo.title || "LRR photo"}
                            loading="lazy"
                        />
                    ))}
                </div>

                <Link to="/gallery" className="view-all-link">
                    See Full Gallery →
                </Link>
            </section>

            {/* === SPONSORS (STATIC) === */}
            <section className="home-section sponsors-preview">
                <h2>🤝 Thank You to Our Sponsors 🤝</h2>
                <h3>Featured Sponsors</h3>

                {!sponsorsData || sponsorsData.length === 0 ? (
                    <p>No sponsors yet. Interested in sponsoring? Contact us!</p>
                ) : (
                    <div className="sponsor-grid">
                        {FEATURED_TIERS.map((tier) => {
                            const sponsor = featuredSponsors.find((s) => s.tier === tier);

                            return sponsor ? (
                                <div key={sponsor.id} className="sponsor-item">
                                    <div className="sponsor-logo-box">
                                        {sponsor.logoUrl ? (
                                            <img
                                                src={sponsor.logoUrl}
                                                alt={sponsor.name}
                                                className="sponsor-logo"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="sponsor-logo placeholder">
                                                {sponsor.name?.[0] || "S"}
                                            </div>
                                        )}
                                    </div>
                                    <div className="sponsor-name">{sponsor.name}</div>
                                </div>
                            ) : (
                                <div key={tier} className="sponsor-item">
                                    <div className="sponsor-logo-box">
                                        <div className="sponsor-logo placeholder">S</div>
                                    </div>
                                    <div className="sponsor-name">{tier}</div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <Link to="/sponsors" className="view-all-link">
                    Meet All Sponsors →
                </Link>
            </section>
        </div>
    );
};

export default Home;